import React, { useMemo, useState, useCallback } from "react";
import "./SuburbsTable.css";
import useTopSuburbsByIndicator from "../../../hooks/useTopSuburbsByIndicator";

function SortIcon({ active, direction }) {
  return (
    <svg
      className={`sort-main-icon${active ? " sort-icon-active" : ""}`}
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 11L9 14L12 11"
        stroke={active && direction === "desc" ? "#3857e0" : "#B1B1B1"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 7L9 4L6 7"
        stroke={active && direction === "asc" ? "#3857e0" : "#B1B1B1"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function capitalizeWords(str) {
  if (!str) return "—";
  return String(str)
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function SuburbsTable({
  selectedCity,
  title = "Best suburbs rental yield",
  primaryIndicator = "rental_yield_pct",
  order = "desc",
  secondaryIndicator = "median_sales_price_last_12_months",
  maxRows = undefined,
  isActiveMember = false,
}) {
  const { data = [], loading = false, error = null } =
    useTopSuburbsByIndicator(selectedCity, primaryIndicator, {
      order,
      limit: 10,
      secondary: secondaryIndicator,
    }) ?? {};

  const fmtPct = (v) =>
    v == null || isNaN(v)
      ? "—"
      : new Intl.NumberFormat("es-CO", {
          style: "percent",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(v) / 100);

  const fmtMoney = (v) =>
    v == null || isNaN(v)
      ? "—"
      : new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: "AUD",
          maximumFractionDigits: 0,
        }).format(Number(v));

  const primaryHeader = useMemo(() => {
    switch (primaryIndicator) {
      case "lt_growth_rate_20y_pct":
        return "LT growth rate (%) 20 years";
      case "median_price_change_5y_pct":
        return "5 years median % change";
      case "vacancy_rate_sqm":
        return "Vacancy rate (%)";
      default:
        return "Median rental yield (%)";
    }
  }, [primaryIndicator]);

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = useCallback(
    (key) => {
      if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      if (sortKey === "value" || sortKey === "secondaryValue") {
        const av = Number(a[sortKey] ?? 0);
        const bv = Number(b[sortKey] ?? 0);
        return av - bv;
      }
      const ax = (a[sortKey] ?? "").toString();
      const bx = (b[sortKey] ?? "").toString();
      return ax.localeCompare(bx, "es", { sensitivity: "base" });
    });
    return sortDir === "asc" ? copy : copy.reverse();
  }, [data, sortKey, sortDir]);

  // Apply maxRows limit if specified
  const displayData = useMemo(() => {
    let result = sortedData;
    
    // Si es usuario free, solo mostrar 1 fila
    if (!isActiveMember) {
      result = sortedData.slice(0, 1);
    } else if (maxRows && maxRows > 0) {
      result = sortedData.slice(0, maxRows);
    }
    
    return result;
  }, [sortedData, maxRows, isActiveMember]);

  return (
    <div className="table-card">
      <div className="main-title" style={{ marginTop: 12 }}>
        <h3 className="main-title__title">{title}</h3>
        <span />
      </div>

      <div className="table-wrapper">
        <div className="table-scroll-container">
          <table className="table">
            <colgroup>
              <col className="col-city" />
              <col className="col-suburb" />
              <col className="col-primary" />
              <col className="col-price" />
            </colgroup>

            <thead>
              <tr>
                <th
                  className="th-sort"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort("city")}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && toggleSort("city")
                  }
                >
                  <div className="header-content">
                    <span className="th-text">City</span>
                    <SortIcon active={sortKey === "city"} direction={sortDir} />
                  </div>
                </th>

                <th
                  className="th-sort"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort("suburb")}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    toggleSort("suburb")
                  }
                >
                  <div className="header-content">
                    <span className="th-text">Suburb</span>
                    <SortIcon
                      active={sortKey === "suburb"}
                      direction={sortDir}
                    />
                  </div>
                </th>

                <th
                  className="th-sort"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort("value")}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && toggleSort("value")
                  }
                >
                  <div className="header-content">
                    <span className="th-text">{primaryHeader}</span>
                    <SortIcon
                      active={sortKey === "value"}
                      direction={sortDir}
                    />
                  </div>
                </th>

                <th
                  className="th-sort"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort("secondaryValue")}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    toggleSort("secondaryValue")
                  }
                >
                  <div className="header-content">
                    <span className="th-text">
                      Median sales price last 12 months
                    </span>
                    <SortIcon
                      active={sortKey === "secondaryValue"}
                      direction={sortDir}
                    />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="table-state">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="table-state">
                    Error: {String(error)}
                  </td>
                </tr>
              ) : displayData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="table-state">
                    No data available for this city.
                  </td>
                </tr>
              ) : (
                displayData.map((item, idx) => {
                  const isPositive = Number(item.value) >= 0;
                  return (
                    <tr key={idx} className="row-rounded">
                      {/* City capitalized */}
                      <td className="cell-rounded left">
                        {capitalizeWords(item.city)}
                      </td>

                      <td className="cell-rounded">{item.suburb}</td>

                      <td className="cell-rounded growth-cell-modern">
                        <span
                          className={
                            "growth-pill-modern " +
                            (isPositive ? "positive" : "negative")
                          }
                          title={fmtPct(item.value)}
                        >
                          {isPositive ? (
                            <svg width="16" height="16" viewBox="0 0 16 16">
                              <path
                                d="M8 13V3M8 3l-3 3M8 3l3 3"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16">
                              <path
                                d="M8 3v10M8 13l-3-3M8 13l3-3"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {fmtPct(item.value)}
                        </span>
                      </td>

                      <td className="cell-rounded right">
                        {fmtMoney(item.secondaryValue)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
